import { StepType } from "@redotech/redo-flows-core-schema-common/common";
import {
  AdvancedFlow,
  SkipCondition,
  TriggerStep,
} from "./advanced-flow-db-parser";
import {
  JudgemeIntegrationTriggerKey,
  LoyaltyLionIntegrationTriggerKey,
  YotpoIntegrationTriggerKey,
} from "./integration-triggers";
import { Integration } from "./integrations";
import { OmsTriggerKey } from "./oms-trigger-key";
import { RuleTriggerType } from "./rules/types";
import { ConditionDataSource } from "./steps";
import type { FlowTeam } from "./team-contracts";
import {
  categories,
  CustomEventTriggerKey,
  EwTriggerKey,
  HsaTriggerKey,
  MarketingAgentCustomKey,
  MarketingTriggerKey,
  ReviewsTriggerKey,
  TnplTriggerKey,
  TriggerKey,
  type Category,
} from "./triggers";

export const AUTOMATIONS_TABLE_CATEGORIES = [
  "Order tracking",
  "Return tracking",
  "Try first",
  "Marketing",
  "Reviews",
  "Extended warranty",
  "Integration",
  "Custom Event",
  "Rules",
  "Recover",
  "HSA",
] as const satisfies Category[];

export const EXCLUDED_CATEGORIES = categories.filter(
  (c) => !(AUTOMATIONS_TABLE_CATEGORIES as readonly string[]).includes(c),
);

export function getEnabledCategoriesForAutomationsFilter(
  team: FlowTeam,
  enabledIntegrations: Record<Integration, boolean>,
): Category[] {
  const hasAnyIntegration = Object.values(enabledIntegrations).some(Boolean);
  const enabledCategories = new Set<Category>();

  if (team.settings.marketing?.enabled) {
    // If marketing is enabled, include marketing AND order tracking regardless of order tracking setting
    enabledCategories.add("Marketing");
    enabledCategories.add("Order tracking");
    enabledCategories.add("Custom Event");

    if (hasAnyIntegration) {
      enabledCategories.add("Integration");
    }
  } else if (team.settings.orderTracking?.enabled) {
    // If only order tracking is enabled (but not marketing), show just order tracking
    enabledCategories.add("Order tracking");
  }

  if (team.settings.returns?.enabled) {
    enabledCategories.add("Return tracking");
  }

  if (team.settings.tnpl?.enabled) {
    enabledCategories.add("Try first");
  }

  if (team.settings.reviews?.enabled) {
    enabledCategories.add("Reviews");
  }

  if (team.settings.hsa?.enabled) {
    enabledCategories.add("HSA");
  }

  return Array.from(enabledCategories);
}

export function getTriggerStep(flow: AdvancedFlow): TriggerStep {
  const triggerStep = flow.steps.find((step) => step.type === StepType.TRIGGER);
  if (!triggerStep) {
    throw new Error("No trigger step found in flow");
  }
  return triggerStep;
}

export function getTriggerKeys(
  flows: readonly AdvancedFlow[],
): Set<TriggerKey> {
  const triggerKeys = new Set<TriggerKey>();
  for (const flow of flows) {
    for (const step of flow.steps) {
      if (step.type === StepType.TRIGGER) {
        triggerKeys.add(step.key);
      }
    }
  }
  return triggerKeys;
}

export function hasTriggerKey(
  flow: AdvancedFlow,
  triggerKeys: Set<TriggerKey>,
): boolean {
  return flow.steps.some(
    (step) => step.type === StepType.TRIGGER && triggerKeys.has(step.key),
  );
}

export function getOmsFlowTriggerKey(flow: AdvancedFlow): OmsTriggerKey {
  const triggerStep = flow.steps.find((step) => step.type === StepType.TRIGGER);
  const key = triggerStep?.key;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return Object.values(OmsTriggerKey).includes(key as OmsTriggerKey)
    ? // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      (key as OmsTriggerKey)
    : OmsTriggerKey.NEW_FULFILLMENT_GROUP;
}

// Individual keys only, never enum spreads: a newly added trigger key must
// default to hidden/skipped until it is deliberately listed here.
const SMART_SENDING_TRIGGER_KEYS: ReadonlySet<TriggerKey> = new Set<TriggerKey>(
  [
    // Marketing automations that smart sending throttles. Campaigns, signups
    // and back-in-stock are absent by design — see triggerKeySupportsSmartSending.
    MarketingTriggerKey.CART_ABANDONED,
    MarketingTriggerKey.BROWSE_ABANDONED,
    MarketingTriggerKey.CHECKOUT_ABANDONED,
    MarketingTriggerKey.BIGCOMMERCE_CART_ABANDONED,
    MarketingTriggerKey.BIGCOMMERCE_CHECKOUT_ABANDONED,
    MarketingTriggerKey.COMMENTSOLD_CART_ABANDONED,
    MarketingTriggerKey.COMMENTSOLD_BROWSE_ABANDONED,
    MarketingTriggerKey.COMMENTSOLD_CHECKOUT_ABANDONED,
    MarketingTriggerKey.EMAIL_SIGNUP_SHOPIFY,
    MarketingTriggerKey.CUSTOMER_GROUP_ENTERED,
    MarketingTriggerKey.CUSTOMER_GROUP_EXITED,
    MarketingTriggerKey.WARRANTY_REGISTRATION,
    MarketingTriggerKey.LOW_INVENTORY,
    MarketingTriggerKey.DATE,
    MarketingTriggerKey.PRICE_DROP,
    MarketingTriggerKey.PRODUCT_SUBSCRIPTION_CANCELLED,
    MarketingTriggerKey.REFUND_RETURN_SUBMITTED,
    MarketingTriggerKey.EXCHANGE_PROCESSED_WITH_CREDIT,
    MarketingTriggerKey.INBOUND_RECEIVED,
    MarketingTriggerKey.GIFT_CARD_BALANCE,
    MarketingAgentCustomKey.MANUAL,
    ReviewsTriggerKey.REVIEW_SUBMITTED,
    JudgemeIntegrationTriggerKey.REVIEW_SUBMITTED,
    EwTriggerKey.EXTENDED_WARRANTY_PURCHASED,
    CustomEventTriggerKey.CUSTOM_EVENT,
    // HSA order emails
    HsaTriggerKey.HSA_ORDER_ELIGIBLE_RECEIPT,
    HsaTriggerKey.HSA_ORDER_LMN_PENDING,
    HsaTriggerKey.HSA_ORDER_LMN_APPROVED,
    // Try First
    TnplTriggerKey.TRIAL_STARTED,
    TnplTriggerKey.ABOUT_TO_REAUTH_CUSTOMER,
    TnplTriggerKey.TRIAL_ITEMS_CANCELLED,
    TnplTriggerKey.REAUTHORIZATION_FAILED,
    TnplTriggerKey.TRIAL_ENDED,
    // Loyalty integrations
    YotpoIntegrationTriggerKey.YOTPO_LOYALTY_EXPIRATION_REMINDER,
    YotpoIntegrationTriggerKey.YOTPO_LOYALTY_POINTS_REMINDER,
    YotpoIntegrationTriggerKey.YOTPO_LOYALTY_REDEMPTION_REMINDER,
    YotpoIntegrationTriggerKey.YOTPO_LOYALTY_REDEMPTION_CREATED,
    YotpoIntegrationTriggerKey.YOTPO_LOYALTY_REFERRAL_COMPLETED,
    YotpoIntegrationTriggerKey.YOTPO_LOYALTY_REFERRAL_SHARED,
    YotpoIntegrationTriggerKey.YOTPO_LOYALTY_CUSTOMER_BIRTHDAY,
    YotpoIntegrationTriggerKey.YOTPO_LOYALTY_OPT_IN,
    YotpoIntegrationTriggerKey.YOTPO_LOYALTY_TIER_EARNED,
    YotpoIntegrationTriggerKey.YOTPO_LOYALTY_TIER_LOST,
    YotpoIntegrationTriggerKey.YOTPO_LOYALTY_POINTS_EARNED,
    YotpoIntegrationTriggerKey.YOTPO_REVIEW_CREATED,
    LoyaltyLionIntegrationTriggerKey.LOYALTY_LION_ENROLLED,
    LoyaltyLionIntegrationTriggerKey.LOYALTY_LION_REWARD_AVAILABLE,
    LoyaltyLionIntegrationTriggerKey.LOYALTY_LION_TIER_UPGRADE,
    LoyaltyLionIntegrationTriggerKey.LOYALTY_LION_APPROACHING_TIER_UPGRADE,
    LoyaltyLionIntegrationTriggerKey.LOYALTY_LION_REFERRAL_COMPLETE,
    LoyaltyLionIntegrationTriggerKey.LOYALTY_LION_RULE_COMPLETED,
    // Support rules keep their current behavior pending a product decision on
    // whether smart sending is meaningful for rule-driven replies.
    RuleTriggerType.CONVERSATION_CREATED,
    RuleTriggerType.CONVERSATION_MESSAGE_CREATED,
    RuleTriggerType.MERCHANT_MESSAGE_CREATED,
    RuleTriggerType.MESSAGE_RECEIVED,
    RuleTriggerType.CONVERSATION_STATUS_CHANGED,
    RuleTriggerType.TAG_ADDED,
    RuleTriggerType.TAG_REMOVED,
    RuleTriggerType.ASSIGNMENT_CHANGED,
    RuleTriggerType.CONVERSATION_PRIORITY_CHANGED,
    RuleTriggerType.CONVERSATION_READ,
    RuleTriggerType.THREAD_CREATED,
    RuleTriggerType.CHAT_CONVERTED_TO_EMAIL,
    RuleTriggerType.CHAT_HANDED_OFF_TO_AGENT,
    RuleTriggerType.CONVERSATION_INSIGHTS_GENERATED,
    RuleTriggerType.USER_AVAILABILITY_CHANGED,
    RuleTriggerType.USER_VOICE_CALL_ANSWERED,
    RuleTriggerType.USER_VOICE_CALL_ENDED,
  ],
);

/**
 * Whether a trigger's flow sends messages subject to smart sending. Gates both
 * the bypass toggle in the trigger inspector and send-time throttling.
 *
 * A key is absent from the allow-list for one of two equally correct reasons:
 * the flow sends no messages at all (checkout visit, tracking page view), or it
 * sends messages that deliberately ignore smart sending because the send was
 * explicitly requested (campaigns, signup confirmations, back-in-stock). Either
 * way nothing exists for a bypass toggle to bypass, so it stays hidden.
 */
export function triggerKeySupportsSmartSending(
  triggerKey: TriggerKey,
): boolean {
  return SMART_SENDING_TRIGGER_KEYS.has(triggerKey);
}

export function shouldSkipSmartSendingForTriggerKey(
  triggerKey: TriggerKey,
): boolean {
  return !triggerKeySupportsSmartSending(triggerKey);
}

export function isTexasSkipCondition({
  condition,
  texasSegmentId,
}: {
  condition: SkipCondition;
  texasSegmentId: string;
}) {
  return (
    (!condition.actionTypes ||
      condition.actionTypes.includes(StepType.SEND_SMS)) &&
    condition.dataSource === ConditionDataSource.ExistingSegment &&
    condition.segmentIds.includes(texasSegmentId)
  );
}

export function doesFlowHaveTexasSegmentExclusion({
  steps,
  texasSegmentId,
}: {
  steps: AdvancedFlow["steps"];
  texasSegmentId: string;
}) {
  const triggerStep = steps.find((step) => step.type === StepType.TRIGGER);
  if (!triggerStep) {
    return false;
  }
  return triggerStep.skipConditions?.conditions.some((condition) =>
    isTexasSkipCondition({ condition, texasSegmentId }),
  );
}
